'use client';
import { Box, BoxProps, SxProps, useTheme } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { BackgroundVariant, OrbVariant, PaddingOption } from '@/lib/CustomBackgroundTypes';

// Re-export types for backward compatibility
export type { BackgroundVariant, OrbVariant };
import {
  mapDisplayOptions,
  StrapiBackgroundOptions,
  StrapiDisplayOptions,
} from '@/lib/DisplayOptionsHelper';

// Import as URLs to avoid SVGR + React dev alias causing "createElement is not a function" in production
import sphereUrl from './sphere.svg?url';
import sphereCoverUrl from './sphereCover.svg?url';

type BackgroundProps = {
  displayOptions?: StrapiDisplayOptions;
  backgroundOptions?: StrapiBackgroundOptions;
  children: ReactNode | ReactNode[];
  containerProps?: BoxProps;
  boxProps?: BoxProps;
};

const getPaddingValue = (padding: PaddingOption, theme: Theme) => {
  switch (padding) {
    case 'xs':
      return theme.spacing(2);
    case 'sm':
      return theme.spacing(4);
    case 'md':
      return theme.spacing(6);
    case 'lg':
      return theme.spacing(10);
    case 'xl':
      return theme.spacing(14);
    default:
      return 0;
  }
};

export const CustomBackground = ({
  displayOptions,
  backgroundOptions,
  children,
  boxProps = {},
  containerProps = {},
}: BackgroundProps) => {
  const theme = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const [hasScrolled, setHasScrolled] = useState(false);
  useEffect(() => {
    const onFirstScroll = () => {
      setHasScrolled(true);
      window.removeEventListener('scroll', onFirstScroll);
    };
    window.addEventListener('scroll', onFirstScroll, { passive: true });
    return () => window.removeEventListener('scroll', onFirstScroll);
  }, []);
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let frame: number | null = null;

    const evaluateStage = () => {
      const current = rootRef.current;
      if (!current) {
        return;
      }

      const rect = current.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;

      if (rect.top >= viewportHeight || rect.bottom <= 0) {
        setScrollStage(null);
        return;
      }

      // Calculate ratios for different parts of the block
      const topY = rect.top;
      const centerY = rect.top + rect.height / 2;
      const bottomY = rect.bottom;

      const topRatio = Math.min(1, Math.max(0, (viewportHeight - topY) / viewportHeight));
      const centerRatio = Math.min(1, Math.max(0, (viewportHeight - centerY) / viewportHeight));
      const bottomRatio = Math.min(1, Math.max(0, (viewportHeight - bottomY) / viewportHeight));

      let nextStage: typeof scrollStage = null;

      // Use bottom for exiting, center for centered, top for entering
      if (bottomRatio >= 0.6) {
        nextStage = 'block-scroll-exiting';
      } else if (centerRatio >= 0.62) {
        nextStage = 'block-scroll-begin-exit';
      } else if (centerRatio >= 0.48) {
        nextStage = 'block-scroll-centered';
      } else if (topRatio >= 0.5) {
        nextStage = 'block-scroll-entering';
      } else if (topRatio >= 0.3) {
        nextStage = 'block-scroll-begin-enter';
      }

      setScrollStage((prev) => (prev === nextStage ? prev : nextStage));
    };

    const scheduleEvaluation = () => {
      if (frame !== null) {
        return;
      }
      frame = window.requestAnimationFrame(() => {
        frame = null;
        evaluateStage();
      });
    };

    scheduleEvaluation();
    window.addEventListener('scroll', scheduleEvaluation, { passive: true });
    window.addEventListener('resize', scheduleEvaluation);

    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', scheduleEvaluation);
      window.removeEventListener('resize', scheduleEvaluation);
    };
  }, []);

  const [scrollStage, setScrollStage] = useState<
    | 'block-scroll-begin-enter'
    | 'block-scroll-entering'
    | 'block-scroll-centered'
    | 'block-scroll-begin-exit'
    | 'block-scroll-exiting'
    | null
  >(null);

  // Map Strapi options to props with defaults
  const mappedOptions = useMemo(() => {
    return mapDisplayOptions(displayOptions, backgroundOptions);
  }, [displayOptions, backgroundOptions]);

  const variant = useMemo(
    () => mappedOptions.variant?.replaceAll('_', '-').toLowerCase() as BackgroundVariant,
    [mappedOptions.variant]
  );
  const orbVariant = useMemo(
    () => mappedOptions.orbVariant?.replaceAll('_', '-').toLowerCase() as OrbVariant,
    [mappedOptions.orbVariant]
  );

  const { orbCount, orbColor, orbSide } = useMemo(() => {
    if (orbVariant === 'none') {
      return { orbCount: 0, orbColor: null, orbSide: null };
    }
    const parts = orbVariant.split('-');
    return {
      orbCount: parts.length > 1 ? parseInt(parts[1], 10) : 0,
      orbColor: parts[0] ?? null,
      orbSide: parts.length > 2 ? parts[2] : null,
    };
  }, [orbVariant]);

  const orbClasses = useMemo(
    () =>
      [
        boxProps.className,
        `container-${mappedOptions.width}`,
        `orbs-${orbVariant}`,
        `background-${variant}`,
        orbCount > 0 ? `orbs-${orbCount}` : null,
        orbColor ? `orbs-${orbColor}` : null,
        orbSide ? `orbs-${orbSide}` : null,
        hasScrolled ? 'scroll-activated' : null,
        scrollStage,
      ]
        .filter((c) => !!c)
        .join(' '),
    [
      mappedOptions.width,
      boxProps.className,
      orbVariant,
      variant,
      orbCount,
      orbColor,
      orbSide,
      hasScrolled,
      scrollStage,
    ]
  );

  const widthValue = useMemo(() => {
    switch (mappedOptions.width) {
      case 'narrow':
        return `${theme.breakpoints.values.md}px`;
      case 'wide':
        return `${theme.breakpoints.values.xl}px`;
      case 'extra-wide':
        return `calc(${theme.breakpoints.values.xl}px + (${theme.spacing(12)} * 2))`;
      default:
        return `${theme.breakpoints.values.lg}px`;
    }
  }, [mappedOptions.width, theme]);

  const paddingTopValue = useMemo(
    () => getPaddingValue(mappedOptions.specifiedPaddingTop || 'none', theme),
    [mappedOptions.specifiedPaddingTop, theme]
  );
  const paddingBottomValue = useMemo(
    () => getPaddingValue(mappedOptions.specifiedPaddingBottom || 'none', theme),
    [mappedOptions.specifiedPaddingBottom, theme]
  );
  const paddingInlineValue = useMemo(() => {
    const raw = mappedOptions.specifiedPaddingInline;

    if (raw === 'none') {
      return 0;
    }
    return getPaddingValue(raw, theme);
  }, [mappedOptions.specifiedPaddingInline, theme]);

  // Apply background styles directly via sx prop
  const backgroundStyles = useMemo(() => {
    const styles: SxProps<Theme> = {
      position: 'relative',
      flexGrow: 1,
      minHeight: '100%',
      maxHeight: '100%',
      width: '100%',
      boxSizing: 'border-box',
      overflow: backgroundOptions?.BackgroundOrbsOverflow === true ? 'visible' : 'hidden',
      maxWidth: mappedOptions.containedBackground ? widthValue : '100%',
      borderRadius: mappedOptions.containedBackground ? 4 : 0,
      paddingTop: paddingTopValue ?? 0,
      paddingBottom: paddingBottomValue ?? 0,
      paddingInline: paddingInlineValue ?? 0,
      marginTop: mappedOptions.containedBackground ? (paddingTopValue ?? 0) : 0,
      marginBottom: mappedOptions.containedBackground ? (paddingBottomValue ?? 0) : 0,
      marginInline: mappedOptions.containedBackground ? 'auto' : 0,
    };

    // Apply background color directly
    switch (variant) {
      case 'none':
        styles.background = 'none';
        break;
      case 'brand-dark':
        styles.backgroundColor = theme.palette.brand.brandDark;
        styles.color = theme.palette.common.white;
        break;
      case 'primary-dark':
        styles.backgroundColor = theme.palette.brand.primaryDark;
        styles.color = theme.palette.common.white;
        break;
      case 'primary':
        styles.backgroundColor = theme.palette.brand.primary;
        styles.color = theme.palette.common.white;
        break;
      case 'secondary':
        styles.backgroundColor = theme.palette.brand.secondary;
        styles.color = theme.palette.common.white;
        break;
      case 'purple-900':
        styles.backgroundColor = theme.palette.purple[900];
        styles.color = theme.palette.common.white;
        break;
      case 'purple-800':
        styles.backgroundColor = theme.palette.purple[800];
        styles.color = theme.palette.common.white;
        break;
      case 'purple-500':
        styles.backgroundColor = theme.palette.purple[500];
        break;
      case 'purple-400':
        styles.backgroundColor = theme.palette.purple[400];
        break;
      case 'purple-200':
        styles.backgroundColor = theme.palette.purple[200];
        break;
      case 'purple-100':
        styles.backgroundColor = theme.palette.purple[100];
        break;
      case 'gradient-1':
        styles.background = theme.palette.gradients.gradient1;
        break;
      case 'gradient-2':
        styles.background = theme.palette.gradients.gradient2;
        break;
      case 'gradient-3':
        styles.background = theme.palette.gradients.gradient3;
        break;
      case 'gradient-4':
        styles.background = theme.palette.gradients.gradient4;
        break;
      case 'gradient-cta':
        styles.background = theme.palette.gradients.gradientCta;
        break;
      case 'gradient-primary-dark':
        styles.background = theme.palette.gradients.primaryDark;
        styles.color = theme.palette.common.white;
        break;
      case 'gradient-purple-dark':
        styles.background = theme.palette.gradients.purpleDark;
        styles.color = theme.palette.common.white;
        break;
      case 'gradient-secondary-brand':
        styles.background = theme.palette.gradients.secondaryBrand;
        styles.color = theme.palette.common.white;
        break;
    }

    return styles;
  }, [
    mappedOptions.containedBackground,
    widthValue,
    paddingInlineValue,
    paddingTopValue,
    paddingBottomValue,
    variant,
    theme.palette,
    backgroundOptions?.BackgroundOrbsOverflow,
  ]);

  return (
    <Box
      ref={rootRef}
      // @ts-expect-error - types for data-* are not correct
      data-component={`Background-${variant}-${orbVariant} ${boxProps?.['data-component'] || ''}`}
      {...boxProps}
      className={orbClasses}
      sx={
        {
          ...boxProps.sx,
          ...backgroundStyles,

          ...(backgroundOptions?.FadeInOutContent === true
            ? {
                '&.scroll-activated .content-layer': {
                  transition: 'opacity 0.6s ease-out',
                  opacity: 0,
                },
                '&.scroll-activated.block-scroll-begin-enter .content-layer': {
                  opacity: 1,
                },
                '&.scroll-activated.block-scroll-entering .content-layer': {
                  opacity: 1,
                },
                '&.scroll-activated.block-scroll-centered .content-layer': {
                  opacity: 1,
                },
                '&.scroll-activated.block-scroll-begin-exit .content-layer': {
                  opacity: 1,
                },
                '&.scroll-activated.block-scroll-exiting .content-layer': {
                  opacity: 0,
                },
              }
            : {}),

          ...(backgroundOptions?.AnimateOrbs === true
            ? {
                '&.scroll-activated .orb.orb': {
                  opacity: 0,
                  translate: '0 200%',
                  transition: 'translate 1s ease-out, opacity 0.5s ease-out',
                },
                '&.scroll-activated .orb.orb.orb-1': {
                  transitionDelay: '0s',
                  transitionDuration: '0.9s',
                },
                '&.scroll-activated .orb.orb.orb-2': {
                  transitionDelay: '0.1s',
                  transitionDuration: '0.8s',
                },
                '&.scroll-activated .orb.orb.orb-3': {
                  transitionDelay: '0.2s',
                  transitionDuration: '0.7s',
                },
                '&.scroll-activated .orb.orb.orb-4': {
                  transitionDelay: '0.3s',
                  transitionDuration: '0.8s',
                },
                '&.scroll-activated .orb.orb.orb-5': {
                  transitionDelay: '0.45s',
                  transitionDuration: '0.7s',
                },
                '&.scroll-activated .orb.orb.orb-6': {
                  transitionDelay: '0.2s',
                  transitionDuration: '0.6s',
                },
                '&.scroll-activated .orb.orb.orb-7': {
                  transitionDelay: '0.15s',
                  transitionDuration: '0.7s',
                },
                '&.scroll-activated .orb.orb.orb-8': {
                  transitionDelay: '0.3s',
                  transitionDuration: '0.6s',
                },
                '&.scroll-activated .orb.orb.orb-9': {
                  transitionDelay: '0.5s',
                  transitionDuration: '0.5s',
                },
                '&.scroll-activated.block-scroll-entering .orb.orb': {
                  translate: '0 0',
                  opacity: 1,
                },
                '&.scroll-activated.block-scroll-centered .orb.orb': {
                  translate: '0 0',
                  transitionDelay: '0s',
                  opacity: 1,
                },
                '&.scroll-activated.block-scroll-begin-exit .orb.orb': {
                  translate: '0 -300%',
                  transitionDelay: '0s',
                  opacity: 1,
                  '&.orb.orb.orb-1': {
                    transitionDelay: '0s',
                  },
                  '&.orb.orb.orb-2': {
                    transitionDelay: '0.1s',
                  },
                  '&.orb.orb.orb-3': {
                    transitionDelay: '0.15s',
                  },
                  '&.orb.orb.orb-4': {
                    transitionDelay: '0.17s',
                  },
                  '&.orb.orb.orb-5': {
                    transitionDelay: '0.19s',
                  },
                  '&.orb.orb.orb-6': {
                    transitionDelay: '0.21s',
                  },
                  '&.orb.orb.orb-7': {
                    transitionDelay: '0.23s',
                  },
                  '&.orb.orb.orb-8': {
                    transitionDelay: '0.25s',
                  },
                  '&.orb.orb.orb-9': {
                    transitionDelay: '0.27s',
                  },
                },
                '&.scroll-activated.block-scroll-exiting .orb.orb': {
                  translate: '0 -300%',
                  transitionDelay: '0s',
                  opacity: 0,
                },
                '@media (prefers-reduced-motion)': {
                  '.orb.orb': {
                    opacity: 1,
                    translate: 'none',
                    transition: 'none',
                  },
                },
              }
            : {}),

          '&.background-brand-dark, &.background-primary-dark, &.background-primary, &.background-secondary, &.background-purple-900, &.background-purple-800, &.background-gradient-primary-dark, &.background-gradient-purple-dark, &.background-gradient-secondary-brand':
            {
              'a, a:visited': {
                color: `${theme.palette.common.white} !important`,
                '&:hover': {
                  color: `${theme.palette.brand.secondary} !important`,
                },
              },
              'a.MuiButton-root': {
                color: `${theme.palette.common.white} !important`,
              },
            },

          '&.background-orb-cover': {
            position: 'relative',
            '.orb-cover': {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            },
          },

          '& .orb-layer': {
            position: 'absolute',
            top: 0,
            left: '-5%',
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 1,
          },

          // styles
          ' .orb': {
            display: 'block',
            position: 'absolute',
            left: '35%',
            top: '50%',
            width: '60vw',
            height: '60vw',
            overflow: 'hidden',
            content: '""',
            transform: 'translate(-50%, -50%)',
            '&.multi-orb': {
              content: 'unset',
              '&.orb-2': {
                transform: 'rotate(10deg)',
              },
              '&.orb-3': {
                transform: 'rotate(20deg)',
              },
              '&.orb-4': {
                transform: 'rotate(30deg)',
              },
              '&.orb-5': {
                transform: 'rotate(40deg)',
              },
            },
          },

          // single color orbs
          '&.orbs-brand': {
            ' .orb': {
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(theme.palette.brand.brand, 0.6)}, transparent 70%)`,
            },
          },
          '&.orbs-brandDark,&.orbs-branddark': {
            ' .orb': {
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(
              theme.palette.brand.brandDark,
              0.8
            )}, transparent 70%)`,
            },
          },
          '&.orbs-primary': {
            ' .orb': {
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(
              theme.palette.brand.primary,
              0.6
            )}, transparent 70%)`,
            },
          },
          '&.orbs-secondary': {
            ' .orb': {
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(
              theme.palette.brand.secondary,
              0.4
            )}, transparent 70%)`,
            },
          },
          '&.orbs-purple900': {
            ' .orb': {
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(theme.palette.purple[900], 0.8)}, transparent 70%)`,
            },
          },
          '&.orbs-purple800': {
            ' .orb': {
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(theme.palette.purple[800], 0.8)}, transparent 70%)`,
            },
          },
          '&.orbs-purple700': {
            ' .orb': {
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(theme.palette.purple[700], 1)}, transparent 70%)`,
            },
          },
          '&.orbs-purple600': {
            ' .orb': {
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(theme.palette.purple[600], 0.8)}, transparent 70%)`,
            },
          },
          '&.orbs-purple500': {
            ' .orb': {
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(theme.palette.purple[500], 0.8)}, transparent 70%)`,
            },
          },
          '&.orbs-white': {
            ' .orb': {
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(
              theme.palette.common.white,
              0.8
            )}, transparent 70%)`,
            },
          },
          '&.orbs-mixed': {
            ' .orb-1, .orb-6': {
              // secondary
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(
              theme.palette.brand.secondary,
              0.4
            )}, transparent 70%)`,
            },
            ' .orb-2, .orb-5, .orb-8': {
              // primarySecondary1
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(
              theme.palette.brand.primarySecondary1,
              0.4
            )}, transparent 70%)`,
            },
            ' .orb-3, .orb-7': {
              // brand
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(theme.palette.brand.brand, 0.4)}, transparent 70%)`,
            },
            ' .orb-4, .orb-9': {
              // brandDark
              backgroundImage: `
            radial-gradient(at 50% 50%, ${alpha(
              theme.palette.common.white,
              0.4
            )}, transparent 70%)`,
            },
          },

          // positions
          '&.orbs-1.orbs-right': {
            '.orb-1': {
              left: 'unset',
              right: '35%',
              transform: 'translate(50%, -50%)',
            },
          },
          '&.orbs-1.orbs-top': {
            '.orb-1': {
              left: '50%',
              top: '0%',
              transform: 'translate(-50%, -50%)',
            },
          },
          '&.orbs-1.orbs-bottom': {
            '.orb-1': {
              left: '50%',
              top: '100%',
              transform: 'translate(-50%, -50%)',
            },
          },
          '&.orbs-1.orbs-center': {
            '.orb-1': {
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            },
          },

          '&.orbs-2.orbs-left,&.orbs-2.orbs-right': {
            '.orb-1': {
              left: '30%',
              top: '20%',
              width: '45vw',
              height: '45vw',
            },
            '.orb-2': {
              left: 'unset',
              top: 'unset',
              right: '22%',
              bottom: '10%',
              width: '20vw',
              height: '20vw',
              transform: 'translate(50%, 50%)',
            },
          },

          '&.orbs-2.orbs-right': {
            '.orb-1': {
              left: 'unset',
              right: '33%',
              transform: 'translate(50%, -50%)',
            },
            '.orb-2': {
              left: '20%',
              transform: 'translate(-50%, 50%)',
            },
          },

          '&.orbs-3.orbs-left,&.orbs-3.orbs-right': {
            '.orb-1': {
              left: '30%',
              top: '40%',
              width: '40vw',
              height: '40vw',
            },
            '.orb-2': {
              left: 'unset',
              top: 'unset',
              right: '22%',
              bottom: '10%',
              width: '20vw',
              height: '20vw',
              transform: 'translate(50%, 50%)',
            },
            '.orb-3': {
              left: 'unset',
              right: '33%',
              top: '5%',
              width: '15vw',
              height: '15vw',
              transform: 'translate(50%, -50%)',
            },
          },

          '&.orbs-3.orbs-right': {
            '.orb-1': {
              left: 'unset',
              right: '32%',
              transform: 'translate(50%, -50%)',
            },
            '.orb-2': {
              left: '20%',
              transform: 'translate(-50%, 50%)',
            },
            '.orb-3': {
              left: '33%',
              transform: 'translate(-50%, -50%)',
            },
          },

          '&.orbs-5.orbs-left,&.orbs-5.orbs-right,&.orbs-7.orbs-left,&.orbs-7.orbs-right,&.orbs-9.orbs-left,&.orbs-9.orbs-right':
            {
              '.orb-1': {
                left: '28%',
                top: '27%',
                width: '35vw',
                height: '35vw',
              },
              '.orb-2': {
                left: 'unset',
                top: 'unset',
                right: '20%',
                bottom: '18%',
                width: '30vw',
                height: '30vw',
                transform: 'translate(50%, 50%)',
              },
              '.orb-3': {
                left: 'unset',
                right: '23%',
                top: '15%',
                width: '20vw',
                height: '20vw',
                transform: 'translate(50%, -50%)',
              },
              '.orb-4': {
                left: '20%',
                top: '65%',
                width: '20vw',
                height: '20vw',
              },
              '.orb-5': {
                left: '35%',
                top: '50%',
                width: '15vw',
                height: '15vw',
              },
            },

          '&.orbs-5.orbs-right,&.orbs-7.orbs-right,&.orbs-9.orbs-right': {
            '.orb-1': {
              left: 'unset',
              right: '28%',
              transform: 'translate(50%, -50%)',
            },
            '.orb-2': {
              right: 'unset',
              left: '20%',
              transform: 'translate(-50%, 50%)',
            },
            '.orb-3': {
              right: 'unset',
              left: '23%',
              transform: 'translate(-50%, -50%)',
            },
            '.orb-4': {
              left: 'unset',
              right: '20%',
              transform: 'translate(50%, -50%)',
            },
            '.orb-5': {
              left: 'unset',
              right: '35%',
              transform: 'translate(50%, -50%)',
            },
          },

          '&.orbs-7.orbs-left,&.orbs-7.orbs-right,&.orbs-9.orbs-left,&.orbs-9.orbs-right': {
            '.orb-6': {
              left: 'unset',
              top: 'unset',
              right: '30%',
              bottom: '40%',
              width: '20vw',
              height: '20vw',
              transform: 'translate(50%, 50%)',
            },
            '.orb-7': {
              top: 'unset',
              bottom: '10%',
              left: '30%',
              width: '25vw',
              height: '25vw',
              transform: 'translate(-50%, 50%)',
            },
          },

          '&.orbs-7.orbs-right,&.orbs-9.orbs-right': {
            '.orb-6': {
              right: 'unset',
              left: '30%',
              transform: 'translate(-50%, 50%)',
            },
            '.orb-7': {
              left: 'unset',
              right: '30%',
              transform: 'translate(50%, 50%)',
            },
          },

          '&.orbs-9.orbs-left,&.orbs-9.orbs-right': {
            '.orb-8': {
              top: '5%',
              left: '45%',
              width: '28vw',
              height: '28vw',
            },
            '.orb-9': {
              left: 'unset',
              right: '38%',
              top: '35%',
              width: '22vw',
              height: '22vw',
              transform: 'translate(50%, -50%)',
            },
          },

          '&.orbs-9.orbs-right': {
            '.orb-8': {
              left: 'unset',
              right: '45%',
              transform: 'translate(50%, -50%)',
            },
            '.orb-9': {
              right: 'unset',
              left: '38%',
              transform: 'translate(-50%, -50%)',
            },
          },

          '&.orbs-center': {
            '.orb': {
              left: '50%',
              top: '50%',
              width: '60vw',
              height: '60vw',
              transform: 'translate(-50%, -50%)',
            },
          },
        } as SxProps<Theme>
      }
    >
      {mounted && variant === 'orb-cover' && (
        /* eslint-disable-next-line @next/next/no-img-element -- dynamic orb cover URL, not a static asset */
        <img
          src={sphereCoverUrl}
          className="orb-cover"
          alt=""
          aria-hidden
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      {mounted && orbVariant !== null && (
        <>
          {orbCount > 0 && (
            <Box className="orb-layer">
              {Array.from({ length: orbCount }).map((_, index) =>
                orbColor === 'multi' ? (
                  /* eslint-disable-next-line @next/next/no-img-element -- dynamic orb URL, not a static asset */
                  <img
                    key={index}
                    src={sphereUrl}
                    className={`multi-orb orb orb-${index + 1}`}
                    alt=""
                    aria-hidden
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <Box key={index} className={`orb orb-${index + 1}`} />
                )
              )}
            </Box>
          )}
        </>
      )}

      <Box
        className="content-layer"
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          {...containerProps}
          className={[
            containerProps?.className ?? '',
            mappedOptions.containedBackground ? 'contained-bg' : '',
            mappedOptions.centerAlignTitles ? 'content-center-align-titles' : '',
            mappedOptions.centerAlignParagraphs ? 'content-center-align-paragraphs' : '',
            mappedOptions.centerAlignImages ? 'content-center-align-images' : '',
          ].join(' ')}
          sx={{
            ...(containerProps?.sx ?? {}),
            position: 'relative',
            zIndex: 2,
            maxWidth: widthValue,
            marginInline: 'auto',
            '&.contained-bg': {
              paddingTop: paddingTopValue ?? 0,
              paddingBottom: paddingBottomValue ?? 0,
              maxWidth: '100%',
            },
            '&.content-center-align-titles': {
              'h1,h2,h3,h4,h5,h6': {
                textAlign: 'center',
              },
            },
            '&.content-center-align-paragraphs': {
              p: {
                textAlign: 'center',
              },
            },
            '&.content-center-align-images': {
              'img,media,.strapi-image-box': {
                textAlign: 'center',
                justifySelf: 'center',
                alignSelf: 'center',
                marginInline: 'auto',
              },
            },
          }}
          data-component="CustomBackgroundContent"
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};
